import {createBrowserRouter, RouterProvider, Navigate, useLocation, useNavigate, useRouteError} from "react-router-dom";
import React, {useContext, createContext, useEffect} from 'react';
import {AuthContext, AuthProvider} from "./context/AuthContext";
import {FetchContext, FetchProvider} from "./context/FetchContext";
import Page from "./components/Page";
import BackgroundUploadsProvider from "./storage/BackgroundUploadsProvider";
import {HeartbeatProvider} from "./context/HeartbeatContext";
import { SessionEventsProvider } from "./context/SessionEventsContext";
import { trackSessionEvent } from "./analytics/sessionEvents";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SignalWifiOffOutlinedIcon from "@mui/icons-material/SignalWifiOffOutlined";
import {NewLogoIcon} from "./pages/NewHome/HomeIcons";
import SignInUserIntercomComponent from "./components/IntercomComponent";
import PrivacyPolicy from "./pages/Landing/PrivacyPolicy";
import TermsOfService from "./pages/Landing/Terms";
import NewStudioAndLobby from "./pages/NewStudio/NewStudioAndLobby";
import NewGuestStudioAndLobby from "./pages/NewGuestStudio/NewGuestStudioAndLobby";
import AgenticHome from "./pages/NewHome/AgenticHome";
import V2Editor from "./pages/V2Editor/V2Editor";
import V2InitialLandingPage from "./pages/V2InitialLandingPage/V2InitialLandingPage";
import V2SpeechCleanupLandingPage from "./pages/V2InitialLandingPage/V2SpeechCleanupLandingPage";

const CHUNK_RELOAD_GUARD_KEY = "__libretto_chunk_reload_guard_v2__";
const CHUNK_RELOAD_GUARD_TTL_MS = 5 * 60 * 1000;
const isChunkLoadErrorMessage = (message) =>
  /Loading chunk .* failed/i.test(message) ||
  /ChunkLoadError/i.test(message) ||
  /Failed to fetch dynamically imported module/i.test(message);

const tryRecoverFromChunkError = (rawError) => {
  const message = (() => {
    try {
      if (typeof rawError === "string") return rawError;
      if (rawError && typeof rawError === "object" && "message" in rawError) return String(rawError.message);
      return String(rawError ?? "");
    } catch {
      return "";
    }
  })();

  if (!isChunkLoadErrorMessage(message)) return;

  try {
    if (!navigator.onLine) return;
  } catch {}

  try {
    const now = Date.now();
    const signature = String(message ?? "").slice(0, 400);
    const raw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const lastSignature = String(parsed?.signature ?? "");
    const lastAt = Number(parsed?.at ?? 0);
    const isFreshRepeat =
      signature &&
      lastSignature === signature &&
      Number.isFinite(lastAt) &&
      now - lastAt < CHUNK_RELOAD_GUARD_TTL_MS;
    if (isFreshRepeat) return;

    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, JSON.stringify({ signature, at: now }));

    const url = new URL(window.location.href);
    url.searchParams.set("__reload", String(now));
    window.location.replace(url.toString());
  } catch {}
};

// NOTE: Avoid route-level lazy loading/code splitting.
// Missing chunk errors are extremely user-visible and can happen during deploy/cache mismatch windows.

const AuthenticatedRoute = ({children}) => {
  const auth = useContext(AuthContext);
  const location = useLocation();

  if (!auth.authReady) {
    return null;
  }

  if (auth.isUserAuthenticated()) {
    const intercomUser = auth.getUser?.();

    // Inactive subscribers are gated to dashboard and prompted via the settings modal there.
    if (!auth.isUserSubscriptionActive()) {
      if (location.pathname !== "/dashboard") {
        return <Navigate to="/dashboard?settings=plan" replace={true}/>
      }
    }

    return (
      <HeartbeatProvider>
        {children}
      </HeartbeatProvider>
    );
  }
  return (
      <Navigate to="/" replace={true}/>
  );
};

const RouteErrorPage = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  const errorMessage = (() => {
    try {
      if (typeof error === 'string') return error;
      if (error && typeof error === 'object' && 'message' in error) return String(error.message);
      return String(error);
    } catch {
      return 'Unknown error';
    }
  })();

  const isOffline = (() => {
    try {
      return !navigator.onLine;
    } catch {
      return false;
    }
  })();

		  const isChunkLoadError =
		    /Loading chunk .* failed/i.test(errorMessage) ||
		    /ChunkLoadError/i.test(errorMessage) ||
		    /Failed to fetch dynamically imported module/i.test(errorMessage);

			  const title = isOffline ? 'You are offline' : 'That didn’t work';
			  const subtitle = isOffline
		    ? (isChunkLoadError
		        ? 'Reconnect to the internet to open this page.'
		        : 'Reconnect and try again.')
		    : 'Try reloading — this usually fixes it.';

		  // Auto-recover once per chunk-error signature (with TTL) to avoid reload loops.
		  useEffect(() => {
		    if (!isChunkLoadError) return;
		    if (isOffline) return;
		    try {
		      const now = Date.now();
		      const signature = String(errorMessage ?? "").slice(0, 400);
		      const raw = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY);
		      const parsed = raw ? JSON.parse(raw) : null;
		      const lastSignature = String(parsed?.signature ?? "");
		      const lastAt = Number(parsed?.at ?? 0);
		      const isFreshRepeat =
		        signature &&
		        lastSignature === signature &&
		        Number.isFinite(lastAt) &&
		        now - lastAt < CHUNK_RELOAD_GUARD_TTL_MS;
		      if (isFreshRepeat) return;

		      sessionStorage.setItem(
		        CHUNK_RELOAD_GUARD_KEY,
		        JSON.stringify({ signature, at: now })
		      );

		      const url = new URL(window.location.href);
		      url.searchParams.set("__reload", String(now));
		      window.location.replace(url.toString());
		    } catch {}
		  }, [isChunkLoadError, isOffline, errorMessage]);

		  const buttonSx = {
		    borderRadius: '8px',
		    textTransform: 'none',
		    fontFamily: '"Inter", sans-serif',
	    fontSize: '13px',
	    fontWeight: 500,
	    padding: '8px 12px',
	    minHeight: '32px',
	  };

	  return (
	    <Box
	      sx={{
	        position: 'fixed',
	        inset: 0,
	        backgroundColor: 'var(--app-bg-canvas)',
	        display: 'flex',
	        alignItems: 'center',
	        justifyContent: 'center',
	        padding: '24px',
	        zIndex: 9999,
	      }}
	    >
	      <Box
	        sx={{
	          width: '100%',
	          maxWidth: '560px',
	          backgroundColor: 'var(--app-bg-surface)',
	          borderRadius: '16px',
	          border: '1px solid var(--app-border-subtle)',
	          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.08)',
	          padding: '28px',
	        }}
		      >
		        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
		          <Box
		            sx={{
		              width: 28,
		              height: 28,
		              borderRadius: '10px',
		              border: '1px solid var(--app-border-subtle)',
		              backgroundColor: 'var(--app-bg-subtle)',
		              display: 'flex',
		              alignItems: 'center',
		              justifyContent: 'center',
		              flexShrink: 0,
		            }}
		          >
		            {isOffline ? (
		              <SignalWifiOffOutlinedIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />
		            ) : (
		              <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />
		            )}
		          </Box>
		          <Box sx={{ fontFamily: '"Inter", sans-serif', fontSize: '16px', fontWeight: 600, color: 'var(--app-text-primary)' }}>
		            {title}
		          </Box>
		        </Box>
	        <Box sx={{ fontFamily: '"Inter", sans-serif', fontSize: '13px', color: 'var(--app-text-muted)', mb: 2 }}>
	          {subtitle}
	        </Box>
	        <Box sx={{ display: 'flex', gap: 1 }}>
	          <Button
	            variant="contained"
	            disableElevation
	            onClick={() => window.location.reload()}
	            sx={{
	              ...buttonSx,
	              backgroundColor: '#000000',
	              '&:hover': { backgroundColor: '#111111' },
	            }}
	          >
	            Reload
	          </Button>
	          <Button
	            variant="outlined"
	            onClick={() => navigate('/dashboard')}
	            sx={{
	              ...buttonSx,
	              color: 'var(--app-text-primary)',
	              borderColor: 'var(--app-border-subtle)',
	              '&:hover': {
	                borderColor: 'var(--app-border-muted)',
	                backgroundColor: 'var(--app-bg-subtle)',
	              },
	            }}
		          >
		            Go to dashboard
		          </Button>
		        </Box>
		      </Box>
	    </Box>
	  );
	};

if (typeof process === 'undefined') {
  global.process = {
    env: { NODE_ENV: 'development' }
    // You can add more properties to this object if needed
  };
}
const UnAuthenticatedRoute = ({children, isCatchAll}) => {
  const auth = useContext(AuthContext);

  if (!auth.authReady) {
    return isCatchAll ? null : children;
  }

  if (isCatchAll) {
    return auth.isUserAuthenticated()
        ? <Navigate to="/dashboard" replace={true}/>
        : <Navigate to="/" replace={true}/>;
  }


  if (!auth.isUserAuthenticated()) {
    return children;
  }
  return (
      <Navigate to="/dashboard" replace={true}/>
  );
}

// Screen size context for components that need to know about small screen state
export const ScreenSizeContext = createContext({
  isSmallScreen: false,
});

// Hook to use screen size context
export const useScreenSize = () => useContext(ScreenSizeContext);

// Small screen blocker UI component
const SmallScreenBlocker = () => {
  const auth = useContext(AuthContext);

  const handleLogout = async () => {
    try {
      await auth?.logout?.();
    } catch {}
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--app-bg-canvas)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          maxWidth: '400px',
          textAlign: 'center',
        }}
      >
        {/* Libretto Logo */}
        <Box sx={{ mb: 3 }}>
            <NewLogoIcon/>
        </Box>
        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '18px',
            fontWeight: 500,
            color: 'var(--app-text-primary)',
            lineHeight: 1.5,
            mb: 2,
          }}
        >
          Please switch to a larger screen to use Libretto
        </Box>
        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: 'var(--app-text-muted)',
            lineHeight: 1.5,
          }}
        >
          Libretto's editor works best on screens 1000px or wider.
        </Box>
        {auth?.authReady && auth?.isUserAuthenticated?.() && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{
                textTransform: "none",
                fontSize: "13px",
                fontWeight: 500,
                borderRadius: "10px",
                px: 2.5,
                py: 0.75,
                borderColor: "var(--app-border-subtle)",
                color: "var(--app-text-primary)",
                "&:hover": {
                  borderColor: "var(--app-border-muted)",
                  backgroundColor: "var(--app-bg-subtle)",
                },
              }}
            >
              Log out
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const isSupportedChromeEdgeOrBrave = () => {
  if (typeof navigator === 'undefined') return true;
  const ua = navigator.userAgent || '';

  // iOS browsers (including Chrome/Edge) use WebKit and are not supported for Studio/Editor.
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  if (isIOS) return false;

  // Prefer UA-CH brands when available (more reliable than parsing userAgent).
  try {
    const brands = navigator.userAgentData?.brands?.map((b) => b.brand) || [];
    const isEdgeBrand = brands.includes('Microsoft Edge');
    const isChromeBrand = brands.includes('Google Chrome');
    const isBraveBrand = brands.includes('Brave');
    if (isEdgeBrand || isChromeBrand || isBraveBrand) return true;
    if (brands.length > 0) return false;
  } catch { }

  const isBrave =
    /Brave\//.test(ua) ||
    (typeof navigator.brave === 'object' && typeof navigator.brave?.isBrave === 'function');
  const isEdge = /Edg\//.test(ua) || /EdgA\//.test(ua);
  const isChrome =
    /Chrome\//.test(ua) &&
    !/Edg\//.test(ua) &&
    !/EdgA\//.test(ua) &&
    !/OPR\//.test(ua) &&
    !/Opera\//.test(ua) &&
    !/Brave\//.test(ua) &&
    !/Vivaldi\//.test(ua);

  return isBrave || isEdge || isChrome;
};

const EdgeTileIcon = () => {
  return (
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: '8px',
        border: '1px solid rgba(17, 24, 39, 0.12)',
        backgroundColor: 'rgba(17, 24, 39, 0.03)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 700,
        color: '#111827',
        lineHeight: 1,
      }}
    >
      e
    </Box>
  );
};

const BrowserBlocker = ({ productName }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const suffix = String(productName ?? '').trim().toLowerCase() || 'unknown';
    trackSessionEvent(`chrome_edge_only_gate_viewed_${suffix}`);
  }, [productName]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--app-bg-canvas)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '680px',
          backgroundColor: 'var(--app-bg-surface)',
          borderRadius: '16px',
          border: '1px solid var(--app-border-subtle)',
          boxShadow: '0 10px 30px rgba(17, 24, 39, 0.08)',
          padding: '28px',
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <NewLogoIcon />
        </Box>

        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '18px',
            fontWeight: 600,
            color: 'var(--app-text-primary)',
            lineHeight: 1.4,
            mb: 1,
          }}
        >
          Please switch to a Chromium-based browser.
        </Box>

        <Box
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: 'var(--app-text-muted)',
            lineHeight: 1.6,
            mb: 3,
          }}
        >
          For this release, Libretto’s {productName} is only verified on Chrome, Edge, and Brave.
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="contained"
            component="a"
            href="https://www.google.com/chrome/"
            target="_blank"
            rel="noreferrer"
            sx={{ textTransform: 'none', borderRadius: '10px', px: 2.5 }}
          >
            Download Chrome
          </Button>
        </Box>

        <Button
          variant="text"
          onClick={() => navigate('/dashboard')}
          sx={{ textTransform: 'none', color: 'var(--app-text-muted)' }}
        >
          Back to home
        </Button>

        <Box sx={{ mt: 2, fontFamily: '"Inter", sans-serif', fontSize: 12, color: 'var(--app-text-subtle)', lineHeight: 1.5 }}>
          Safari and Firefox aren’t supported yet for Studio and Editor.
        </Box>
      </Box>
    </Box>
  );
};

// Screen size provider that wraps content
const ScreenSizeProvider = ({children}) => {
  const isSmallScreen = useMediaQuery('(max-width: 999px)');
  
  return (
    <ScreenSizeContext.Provider value={{ isSmallScreen }}>
      {children}
    </ScreenSizeContext.Provider>
  );
};

// HOC to wrap a route component with screen size blocker
const withScreenBlocker = (Component) => {
  return function WrappedComponent(props) {
    const isSmallScreen = useMediaQuery('(max-width: 999px)');

    // Keep the wrapped component mounted once it has rendered at least once.
    // This prevents disconnects (e.g. LiveKit) if the user temporarily resizes their window.
    const [hasMountedAtLeastOnce, setHasMountedAtLeastOnce] = React.useState(() => !isSmallScreen);
    React.useEffect(() => {
      if (!isSmallScreen) setHasMountedAtLeastOnce(true);
    }, [isSmallScreen]);

    return (
      <>
        {hasMountedAtLeastOnce && <Component {...props} />}
        {isSmallScreen && <SmallScreenBlocker />}
      </>
    );
  };
};

const withChromeEdgeBraveBlocker = (Component, { productName }) => {
  return function WrappedComponent(props) {
    if (!isSupportedChromeEdgeOrBrave()) {
      return <BrowserBlocker productName={productName} />;
    }
    return <Component {...props} />;
  };
};

// Wrapped components that block on small screens
const BlockedV2Editor = withScreenBlocker(withChromeEdgeBraveBlocker(V2Editor, { productName: 'Editor' }));
const BlockedNewStudioAndLobby = withScreenBlocker(withChromeEdgeBraveBlocker(NewStudioAndLobby, { productName: 'Studio' }));
const BlockedNewGuestStudioAndLobby = withScreenBlocker(withChromeEdgeBraveBlocker(NewGuestStudioAndLobby, { productName: 'Studio' }));
const BlockedAgenticHome = withScreenBlocker(AgenticHome);

const router = createBrowserRouter([
    {path: "/privacy", element: <PrivacyPolicy/>, errorElement: <RouteErrorPage />},
    {path: "/logout", element: <UnAuthenticatedRoute><Navigate to="/"/></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/terms", element: <TermsOfService/>, errorElement: <RouteErrorPage />},
    // {path: "/share/:token", element: <SharePage/>},
    {path: "/", element: <UnAuthenticatedRoute><V2SpeechCleanupLandingPage/></UnAuthenticatedRoute>, errorElement: <RouteErrorPage />},
    // {path: "/invite", element: <WorkspaceInviteAccept/>},
    {path: "/editor/:projectId?", element: <AuthenticatedRoute><BlockedV2Editor/></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/guest/:roomName/:userFriendlyRoomName", element: <BlockedNewGuestStudioAndLobby/>, errorElement: <RouteErrorPage />},
    // {path: "/dashboard/:workspaceId", element: <AuthenticatedRoute><BlockedAgenticHome/></AuthenticatedRoute>},
    {path: "/dashboard", element: <AuthenticatedRoute><BlockedAgenticHome/></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {path: "/studio/:projectId?/:title?/:assetId?", element: <AuthenticatedRoute><BlockedNewStudioAndLobby/></AuthenticatedRoute>, errorElement: <RouteErrorPage />},
    {
    path: "*", // Catch-all route
    element: <UnAuthenticatedRoute isCatchAll={true}><V2InitialLandingPage/></UnAuthenticatedRoute>, // Redirect based on login status
    errorElement: <RouteErrorPage />,
  },
]);

const kIsAuthenticated = 'isAuthenticatedOnLibretto';
const kLibrettoToken = 'librettoUserToken';
const kUserEmailVerified = 'librettoUserEmailVerified';

const App = () => {
  useEffect(() => {
    const onError = (event) => {
      tryRecoverFromChunkError(event?.error ?? event?.message);
    };
    const onUnhandledRejection = (event) => {
      tryRecoverFromChunkError(event?.reason);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return (
      <Page>
        <FetchProvider>
          <AuthProvider>
            <SessionEventsProvider>
                  <BackgroundUploadsProvider>
                <ScreenSizeProvider>
                  <RouterProvider router={router}/>
                </ScreenSizeProvider>
              </BackgroundUploadsProvider>
            </SessionEventsProvider>
          </AuthProvider>
        </FetchProvider>
      </Page>
  );
}

export default App;
